import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  Banknote,
  Building2,
  ClipboardList,
  Database,
  FileText,
  FlaskConical,
  Briefcase,
  CalendarClock,
  Handshake,
  KeyRound,
  LayoutDashboard,
  Megaphone,
  Menu,
  Pill,
  Receipt,
  ScrollText,
  Sparkles,
  Store,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type MenuLink = {
  key: string;
  label: string;
  icon: LucideIcon;
  /** `/admin/auth/me` permissions.menuCode — 없으면 인증만으로 노출 */
  menuCode?: string;
  /** true면 마스터·관리(full-access)만 설정 사이드바에 노출 */
  fullAccessOnly?: boolean;
  /** true면 ROLE_MASTER만 설정 사이드바에 노출 (메뉴 설정 관리) */
  masterOnly?: boolean;
};

export type MenuGroup = {
  label: string;
  icon: LucideIcon;
  children: MenuLink[];
};

export type MenuDivider = { type: 'divider' };

export type SidebarMenuItem = MenuLink | MenuGroup | MenuDivider;

/** 일반 관리자 사이드바 — 시연 메뉴 제외 */
export const adminSidebarMenuItems: SidebarMenuItem[] = [
  {
    key: ROUTE_PATHS.DASHBOARD,
    icon: LayoutDashboard,
    label: '대시보드',
  },
  { type: 'divider' },
  {
    label: '기초정보',
    icon: Database,
    children: [
      {
        key: ROUTE_PATHS.INSTITUTIONS,
        icon: Building2,
        label: '병의원',
        menuCode: 'HOSPITAL',
      },
      {
        key: ROUTE_PATHS.PHARMACIES,
        icon: Store,
        label: '문전약국',
        menuCode: 'PHARMACY',
      },
      {
        key: ROUTE_PATHS.PHARMA_COMPANIES,
        icon: Building2,
        label: '제약사',
        menuCode: 'PHARMA_COMPANY',
      },
      {
        key: ROUTE_PATHS.DRUGS,
        icon: Pill,
        label: '의약품',
        menuCode: 'DRUG',
      },
      {
        key: ROUTE_PATHS.SETTLEMENT_FEES,
        icon: Banknote,
        label: '정산 수수료',
        menuCode: 'SETTLEMENT_COMMISSION',
      },
    ],
  },
  { type: 'divider' },
  {
    label: '운영',
    icon: Users,
    children: [
      {
        key: ROUTE_PATHS.USERS,
        icon: Users,
        label: '유저 관리',
        menuCode: 'USER_MANAGEMENT',
      },
      {
        key: ROUTE_PATHS.PARTNER_COMPANY,
        icon: Briefcase,
        label: '업체 관리',
        menuCode: 'PARTNER_COMPANY',
      },
      {
        key: ROUTE_PATHS.CONTRACT_APPENDIX,
        icon: ScrollText,
        label: '계약 별첨 관리',
        menuCode: 'CONTRACT_APPENDIX',
      },
      {
        key: ROUTE_PATHS.BASE_FEE_RATES,
        icon: Banknote,
        label: '기본 수수료율',
        menuCode: 'BASE_FEE_RATE',
      },
      {
        key: ROUTE_PATHS.DETAIL_FEE_RATES,
        icon: Banknote,
        label: '상세 수수료율',
        menuCode: 'DETAIL_FEE_RATE',
      },
      {
        key: ROUTE_PATHS.CLIENTS,
        icon: Handshake,
        label: '거래처',
        menuCode: 'PARTNER',
      },
    ],
  },
  { type: 'divider' },
  {
    label: '처방',
    icon: FileText,
    children: [
      {
        key: ROUTE_PATHS.PRESCRIPTION_REVIEW,
        icon: FileText,
        label: '처방 검수',
        menuCode: 'PRESCRIPTION_REVIEW',
      },
      {
        key: ROUTE_PATHS.PRESCRIPTION_HISTORY,
        icon: FileText,
        label: '확정 처방',
        menuCode: 'FINALIZED_PRESCRIPTION',
      },
      {
        key: ROUTE_PATHS.PRESCRIPTION_REGISTRATION_DEADLINE,
        icon: CalendarClock,
        label: '처방 마감일 설정',
        menuCode: 'PRESCRIPTION_REGISTRATION_DEADLINE',
      },
    ],
  },
  { type: 'divider' },
  {
    label: '정산',
    icon: Banknote,
    children: [
      {
        key: ROUTE_PATHS.SETTLEMENT_UPLOAD,
        icon: Banknote,
        label: '제약사 정산 정보',
        menuCode: 'PHARMA_SETTLEMENT',
      },
      {
        key: ROUTE_PATHS.SETTLEMENT_PRESCRIPTIONS,
        icon: Banknote,
        label: '정산 내역 관리',
        menuCode: 'SETTLEMENT_MANAGEMENT',
      },
      {
        key: ROUTE_PATHS.REVERSE_TAX,
        icon: Receipt,
        label: '세금계산서 역발행',
        menuCode: 'REVERSE_TAX_INVOICE',
      },
      // 정산 시트 생성기(SETTLEMENT_GENERATOR)는 레거시와 같이 메뉴 미노출 —
      // 제약사 정산 정보 페이지 상단 「정산 정보 종합」 버튼으로만 진입
    ],
  },
  { type: 'divider' },
  {
    label: '맥스매치',
    icon: Sparkles,
    children: [
      {
        key: ROUTE_PATHS.MAX_MATCH,
        icon: Sparkles,
        label: '맥스매치',
        menuCode: 'MAX_MATCH_STANDARD',
      },
      {
        key: ROUTE_PATHS.MAX_MATCH_FULL,
        icon: Sparkles,
        label: '맥스매치 Full',
        menuCode: 'MAX_MATCH_FULL',
      },
      {
        key: ROUTE_PATHS.MAX_MATCH_MINI,
        icon: Sparkles,
        label: '맥스매치 Lite',
        menuCode: 'MAX_MATCH_LITE',
      },
      {
        key: ROUTE_PATHS.MAX_MATCH_OPS,
        icon: Sparkles,
        label: '맥스매치 운영',
        menuCode: 'MAX_MATCH_OPERATION',
      },
      {
        key: ROUTE_PATHS.MAX_MATCH_OPERATING_HOURS,
        icon: Sparkles,
        label: '운영 시간',
        menuCode: 'MAX_MATCH_OPERATING_HOURS',
      },
    ],
  },
  { type: 'divider' },
  {
    label: '공지/약관',
    icon: Megaphone,
    children: [
      {
        key: ROUTE_PATHS.BOARD_ANNOUNCEMENTS,
        icon: Megaphone,
        label: '공지사항',
        menuCode: 'NOTICE',
      },
      {
        key: ROUTE_PATHS.BOARD_FAQ,
        icon: FileText,
        label: 'FAQ',
        menuCode: 'FAQ',
      },
      {
        key: ROUTE_PATHS.TERMS,
        icon: ScrollText,
        label: '약관 관리',
        menuCode: 'TERMS',
      },
    ],
  },
];

/** 시연 전용 사이드바 — `/demo` URL에서만 노출 */
export const demoSidebarMenuItems: SidebarMenuItem[] = [
  { key: ROUTE_PATHS.DEMO_HOME, icon: FlaskConical, label: 'OCR 시연' },
  {
    key: ROUTE_PATHS.DEMO_POST_PROCESS,
    icon: FlaskConical,
    label: 'Post Process',
  },
  {
    key: ROUTE_PATHS.DEMO_BATCH_JOBS,
    icon: FlaskConical,
    label: '배치 OCR Job',
  },
];

/**
 * 설정 모드 사이드바 — `/admin/settings/*` 에서 노출.
 * 진입은 헤더 프로필 「설정」만. 표시 순서·항목은 `/admin/auth/me` SETTINGS 권한을 우선한다.
 * (`buildSettingsSidebarFromMenuTree` — 폴백용 정적 목록)
 */
export const settingsSidebarMenuItems: SidebarMenuItem[] = [
  {
    key: ROUTE_PATHS.MY_ACCOUNT,
    icon: UserRound,
    label: '내 프로필',
  },
  {
    key: ROUTE_PATHS.SETTINGS_ACCOUNTS,
    icon: Users,
    label: '계정 관리',
    fullAccessOnly: true,
  },
  {
    key: ROUTE_PATHS.SETTINGS_AUDIT_LOG,
    icon: ClipboardList,
    label: '감사로그',
    fullAccessOnly: true,
  },
  {
    key: ROUTE_PATHS.SETTINGS_PARTNERS,
    icon: KeyRound,
    label: '파트너 관리',
    fullAccessOnly: true,
  },
  {
    key: ROUTE_PATHS.SETTINGS_MENUS,
    icon: Menu,
    label: '메뉴 관리',
    masterOnly: true,
    menuCode: 'MENU_MANAGEMENT',
  },
];
